import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useInView } from "../../../hooks/useInView";
import placeholderLogo from "../../../images/mcdonalds-logo.png";
import { Skeleton } from "../../Ui/Skeleton/Skeleton";
import ProgressiveImage from "../../Ui/ProgressiveImage/ProgressiveImage";
import { citiesMatch, placesListPath, placeDetailPath } from "../../../utils/places";

const PickCardSkeleton = () => (
  <div className="card elev-md sp-place-card" style={{ padding: 0, overflow: "hidden", flex: "0 0 calc((100% - 72px) / 3.5)" /* 3 gaps of 24px between the 3.5 visible cards */ }}>
    <Skeleton height={220} radius={0} />
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>
      <Skeleton width="70%" height={19} />
      <Skeleton width="90%" height={14} />
      <Skeleton width="60%" height={14} />
      <Skeleton width="40%" height={12} />
    </div>
  </div>
);

const PicksSection = ({ selectedCity }) => {
  const { t } = useTranslation();
  const [ref, isInView] = useInView(0, "250px 0px");
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!isInView) return;
    api
      .getPublicCompanies()
      .then((res) => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching companies for landing page:", err))
      .finally(() => setIsLoading(false));
  }, [isInView]);

  const topPicks = useMemo(() => companies.filter((c) => c.isTopPick), [companies]);

  const citiesWithPicks = useMemo(
    () => new Set(topPicks.map((c) => c.city).filter(Boolean).map((c) => c.toLowerCase())),
    [topPicks]
  );
  const effectiveCity = selectedCity;

  const picks = useMemo(
    () => topPicks.filter((c) => !effectiveCity || citiesMatch(c.city, effectiveCity)),
    [topPicks, effectiveCity]
  );

  // Tracks whether there's more to scroll to on either side, so the arrow
  // buttons can disable themselves at the ends instead of scrolling nowhere.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const updateScrollState = () => {
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [picks, isLoading, ref]);

  const scrollByPage = (direction) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  };

  // Hide the whole section if the selected city has no picks — the nav
  // dropdown is the single source of truth for which city is showing, so
  // there's no fallback city to substitute and no point showing an empty
  // grid. Before a city is selected, fall back to "does any city have picks
  // at all".
  const hasContentForSelection = effectiveCity
    ? citiesWithPicks.has(effectiveCity.toLowerCase())
    : citiesWithPicks.size > 0;
  if (!isLoading && !hasContentForSelection) return null;

  return (
    <section id="places" className="sp-section" style={{ paddingLeft: 32, paddingRight: 32 }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="tag tag-accent">{t.landingPicksKicker}</div>
          <div className="sp-picks-heading-row">
            <h2 style={{ marginTop: 10, maxWidth: 560 }}>
              {t.landingPicksHeading.replace("{city}", effectiveCity || "")}
            </h2>
            {/* Mobile-only — sits next to the heading below the 860px
                breakpoint (see LandingPage.css); the desktop CTA below
                (.sp-picks-view-all) is hidden there instead. */}
            {!isLoading && picks.length > 0 && (
              <Link className="btn btn-secondary sp-picks-view-all-mobile" to={placesListPath(effectiveCity)}>
                {t.landingPicksCtaMore}
              </Link>
            )}
          </div>
          <p style={{ opacity: 0.75, maxWidth: 520 }}>{t.landingPicksSubtitle}</p>
        </div>
        {!isLoading && picks.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Link className="btn btn-secondary sp-picks-view-all" to={placesListPath(effectiveCity)}>
              {t.landingPicksCtaMore}
            </Link>
            <div className="sp-picks-arrows" style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="sp-scroll-arrow"
                aria-label="Scroll left"
                disabled={!canScrollLeft}
                onClick={() => scrollByPage(-1)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              </button>
              <button
                type="button"
                className="sp-scroll-arrow"
                aria-label="Scroll right"
                disabled={!canScrollRight}
                onClick={() => scrollByPage(1)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <div
        ref={ref}
        className="sp-cards-scroller"
        style={{ display: "flex", gap: 24, marginTop: 30, overflowX: "auto" }}
      >
        {isLoading && Array.from({ length: 3 }).map((_, i) => <PickCardSkeleton key={i} />)}
        {!isLoading && picks.map((p) => (
          <Link
            key={p._id}
            to={placeDetailPath(effectiveCity, p.slug || p._id)}
            className="card elev-md sp-place-card"
            style={{ padding: 0, overflow: "hidden", textDecoration: "none", color: "inherit", cursor: "pointer", flex: "0 0 calc((100% - 72px) / 3.5)" /* 3 gaps of 24px between the 3.5 visible cards */ }}
          >
            <div style={{ position: "relative" }}>
              <div className="sp-card-media" style={{ borderRadius: 0, height: 220 }}>
                <ProgressiveImage
                  src={p.logoUrl || placeholderLogo}
                  alt={p.name}
                  fallbackSrc={placeholderLogo}
                  loading="lazy"
                  fill
                  imgClassName="washed"
                />
              </div>
              <span className="tag tag-accent" style={{ position: "absolute", top: 14, left: 14 }}>
                {t.landingPicksBadge}
              </span>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="card-title" style={{ fontSize: 19 }}>{p.name}</div>
              <p className="card-body" style={{ fontSize: 14, lineHeight: 1.55 }}>{p.shortDescription}</p>
              <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: "0.03em" }}>{p.location}</div>
            </div>
          </Link>
        ))}
      </div>
      </div>
    </section>
  );
};

export default PicksSection;
